import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { COL_SHIPMENT_LINES, COL_SHIPMENTS } from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { useAuth } from '../../context/AuthContext'
import { matchesListSearch } from '../../lib/listSearch'
import { formatDateRu } from '../../lib/scheduleCalculations'
import { pb } from '../../pocketbase'
import { Alert, ButtonLink, PageHeader, PageShell, SearchField } from '../../components/ui'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'
import type { ShipmentLineRecord, ShipmentRecord } from '../../types/records'

type ShipmentRow = ShipmentRecord & { lineCount: number; totalQty: number }

export function ShipmentsPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<ShipmentRow[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const [shipments, lines] = await Promise.all([
        pb.collection(COL_SHIPMENTS).getFullList<ShipmentRecord>({ sort: '-shippedAt' }),
        pb.collection(COL_SHIPMENT_LINES).getFullList<ShipmentLineRecord>({}),
      ])

      const stats = new Map<string, { count: number; qty: number }>()
      for (const ln of lines) {
        const sid = typeof ln.shipment === 'string' ? ln.shipment : ''
        if (!sid) continue
        const prev = stats.get(sid) ?? { count: 0, qty: 0 }
        prev.count += 1
        prev.qty += Number(ln.quantity) || 0
        stats.set(sid, prev)
      }

      setItems(
        shipments.map((s) => {
          const st = stats.get(s.id) ?? { count: 0, qty: 0 }
          return { ...s, lineCount: st.count, totalQty: st.qty }
        }),
      )
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function removeRow(id: string) {
    if (!window.confirm('Удалить отгрузку и все строки?')) return
    try {
      await pb.collection(COL_SHIPMENTS).delete(id)
      await load()
    } catch (e: unknown) {
      window.alert(pocketbaseErrorMessage(e, 'Ошибка удаления'))
    }
  }

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    return items.filter((row) =>
      matchesListSearch(searchQuery, row.clientName, row.notes, String(row.lineCount), String(row.totalQty)),
    )
  }, [items, searchQuery])

  const shipmentsCsvColumns: CsvColumn<ShipmentRow>[] = useMemo(
    () => [
      { header: 'Дата отгрузки', value: (r) => formatDateRu(r.shippedAt) },
      { header: 'Клиент', value: (r) => r.clientName },
      { header: 'Строк', value: (r) => r.lineCount },
      { header: 'Кол-во', value: (r) => r.totalQty },
      { header: 'Примечание', value: (r) => r.notes ?? '' },
    ],
    [],
  )

  return (
    <PageShell>
      <PageHeader
        title="Отгрузки"
        lead="Регистрация отгрузок готовой продукции клиентам и связь со строками заявок."
      />
      {err && <Alert kind="err">{err}</Alert>}
      <div className={toolbarClass}>
        {can(PERMISSION_KEYS.SHIPMENTS_WRITE) && (
          <ButtonLink to="/shipments/new" variant="primary">
            Новая отгрузка
          </ButtonLink>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        {displayedItems.length > 0 && (
          <ExportCsvButton filename="shipments" rows={displayedItems} columns={shipmentsCsvColumns} />
        )}
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по клиенту…"
            aria-label="Поиск отгрузок"
          />
        </div>
      </div>
      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Дата отгрузки</th>
              <th className={tableHeadClass}>Клиент</th>
              <th className={tableHeadClass}>Строк</th>
              <th className={tableHeadClass}>Кол-во</th>
              <th className={tableHeadClass}>Примечание</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={6}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedItems.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={6}>
                  <em>{items.length === 0 ? 'Нет отгрузок' : 'Ничего не найдено'}</em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedItems.map((row) => (
                <tr key={row.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{formatDateRu(row.shippedAt)}</td>
                  <td className={tableCellClass}>{row.clientName}</td>
                  <td className={tableCellClass}>{row.lineCount}</td>
                  <td className={tableCellClass}>{row.totalQty}</td>
                  <td className={tableCellClass}>{row.notes?.trim() || '—'}</td>
                  <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                    {can(PERMISSION_KEYS.SHIPMENTS_READ) && (
                      <Link to={`/shipments/${row.id}`} className={btn('ghost', 'sm')}>
                        Открыть
                      </Link>
                    )}
                    {can(PERMISSION_KEYS.SHIPMENTS_WRITE) && (
                      <button
                        type="button"
                        className={cn(btn('danger', 'sm'), 'ml-1')}
                        onClick={() => void removeRow(row.id)}
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
